"use client"

import { useEffect, useMemo, useState } from "react"
import { API_ENDPOINTS } from "../../../../constants/config"

const DEFAULT_FILTERS = {
  category: [],
  sub_category: [],
  brand: [],
  size: [],
  color: [],
}

const DEFAULT_SUMMARY = {
  totalItems: 0,
  highRunning: 0,
  stockOutRisk: 0,
  reorderRequired: 0,
  nonMoving: 0,
}

const TAB_QUERY = {
  all: "all",
  "high-running": "high-running",
  "stock-out-risk": "stock-out-risk",
  "non-moving": "non-moving",
}

const ISSUED_FIELD_BY_DAYS = {
  "7d": "issued_7d",
  "30d": "issued_30d",
  "60d": "issued_60d",
  "90d": "issued_90d",
}

const MOVEMENT_LEVELS = ["High", "Medium", "Low", "Dead"]

export default function useInventoryAnalytics() {
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [filterOptions, setFilterOptions] = useState({ ...DEFAULT_FILTERS })
  const [activeTab, setActiveTab] = useState("all")
  const [daysFilter, setDaysFilter] = useState("30d")

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [categories, setCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [brandOptions, setBrandOptions] = useState([])
  const [sizes, setSizes] = useState([])
  const [colors, setColors] = useState([])
  const [inventoryFeatures, setInventoryFeatures] = useState({ color: false, sub_category: false })

  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({ ...DEFAULT_SUMMARY })
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [analysis, setAnalysis] = useState({ byCategorySubCategory: [], byCategoryBrand: [], byCategoryBrandSku: [] })
  const [analysisLoading, setAnalysisLoading] = useState(false)
  const [analysisError, setAnalysisError] = useState(null)

  const [allItems, setAllItems] = useState([])
  const [allItemsLoading, setAllItemsLoading] = useState(true)

  const isColorEnabled = Boolean(inventoryFeatures?.color)
  const isSubCategoryEnabled = Boolean(inventoryFeatures?.sub_category)

  const authHeaders = () => {
    const token = localStorage.getItem("token")
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  const fetchMetadata = async () => {
    const headers = authHeaders()
    if (!headers.Authorization) return
    const safeJson = (r) => (r.ok ? r.json() : Promise.resolve(null))

    await Promise.all([
      fetch(API_ENDPOINTS.inventoryFeatureConfig, { headers })
        .then(safeJson)
        .then((d) => setInventoryFeatures(d?.data?.features ?? { color: false, sub_category: false }))
        .catch(() => setInventoryFeatures({ color: false, sub_category: false })),
      fetch(API_ENDPOINTS.inventoryCategories, { headers }).then(safeJson).then((d) => d && setCategories(d.data || [])).catch(() => {}),
      fetch(API_ENDPOINTS.inventorySizes, { headers }).then(safeJson).then((d) => d && setSizes(d.data || [])).catch(() => {}),
    ])
  }

  const fetchSubCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.inventorySubCategories, { headers: authHeaders() })
      if (!response.ok) return
      const data = await response.json()
      setSubCategories(data.data || [])
    } catch (err) {
      console.error("Error fetching sub-categories:", err)
    }
  }

  const fetchColors = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.inventoryColors, { headers: authHeaders() })
      if (!response.ok) return
      const data = await response.json()
      setColors(data.data || [])
    } catch (err) {
      console.error("Error fetching colors:", err)
    }
  }

  const buildFilterParams = () => {
    const params = new URLSearchParams()
    if (debouncedSearchTerm) params.append("search", debouncedSearchTerm)
    if (filterOptions.category.length) params.append("category", filterOptions.category.join(","))
    if (filterOptions.sub_category.length) params.append("sub_category", filterOptions.sub_category.join(","))
    if (filterOptions.brand.length) params.append("brand", filterOptions.brand.join(","))
    if (filterOptions.size.length) params.append("size", filterOptions.size.join(","))
    if (filterOptions.color.length) params.append("color", filterOptions.color.join(","))
    return params
  }

  const fetchDashboard = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = buildFilterParams()
      params.append("tab", TAB_QUERY[activeTab] || "all")
      params.append("page", currentPage)
      params.append("limit", itemsPerPage)
      params.append("paginate", "true")

      const response = await fetch(`${API_ENDPOINTS.inventoryAnalyticsDashboard}?${params.toString()}`, {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error(`Failed to fetch analytics: ${response.statusText}`)

      const data = await response.json()
      setItems(data.data || [])
      setSummary({ ...DEFAULT_SUMMARY, ...(data.summary || {}) })
      setTotalCount(data.count ?? (data.data || []).length)
      setBrandOptions(data.meta?.brandOptions || [])
    } catch (err) {
      console.error("Error fetching inventory analytics dashboard:", err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchAnalysis = async () => {
    try {
      setAnalysisLoading(true)
      setAnalysisError(null)

      const params = buildFilterParams()
      const response = await fetch(`${API_ENDPOINTS.inventoryAnalyticsAnalysis}?${params.toString()}`, {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error(`Failed to fetch analysis: ${response.statusText}`)

      const data = await response.json()
      setAnalysis({
        byCategorySubCategory: data.data?.byCategorySubCategory || [],
        byCategoryBrand: data.data?.byCategoryBrand || [],
        byCategoryBrandSku: data.data?.byCategoryBrandSku || [],
      })
    } catch (err) {
      console.error("Error fetching inventory analytics analysis:", err)
      setAnalysisError(err.message)
    } finally {
      setAnalysisLoading(false)
    }
  }

  const fetchAllItems = async () => {
    try {
      setAllItemsLoading(true)

      const params = buildFilterParams()
      params.append("tab", "all")

      const response = await fetch(`${API_ENDPOINTS.inventoryAnalyticsDashboard}?${params.toString()}`, {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error(`Failed to fetch analytics overview: ${response.statusText}`)

      const data = await response.json()
      setAllItems(Array.isArray(data.data) ? data.data : [])
      setBrandOptions(data.meta?.brandOptions || [])
    } catch (err) {
      console.error("Error fetching inventory analytics overview:", err)
      setAllItems([])
    } finally {
      setAllItemsLoading(false)
    }
  }

  useEffect(() => {
    fetchMetadata()
  }, [])

  useEffect(() => {
    if (isSubCategoryEnabled) fetchSubCategories()
    else setSubCategories([])
  }, [isSubCategoryEnabled])

  useEffect(() => {
    if (isColorEnabled) fetchColors()
    else setColors([])
  }, [isColorEnabled])

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 350)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    setFilterOptions((prev) => (prev.sub_category.length === 0 ? prev : { ...prev, sub_category: [] }))
  }, [filterOptions.category])

  useEffect(() => {
    setCurrentPage(1)
  }, [filterOptions, debouncedSearchTerm, activeTab])

  useEffect(() => {
    if (activeTab === "analysis") return
    fetchDashboard()
  }, [filterOptions, debouncedSearchTerm, activeTab, currentPage, itemsPerPage])

  useEffect(() => {
    if (activeTab === "analysis") fetchAnalysis()
  }, [filterOptions, debouncedSearchTerm, activeTab])

  useEffect(() => {
    fetchAllItems()
  }, [filterOptions, debouncedSearchTerm])

  const subCategoryOptions = useMemo(() => {
    if (filterOptions.category.length === 0) return subCategories
    const selectedCategoryIds = categories
      .filter((c) => filterOptions.category.includes(c.name))
      .map((c) => String(c._id))
    return subCategories.filter((sc) => selectedCategoryIds.includes(String(sc.category)))
  }, [categories, filterOptions.category, subCategories])

  const charts = useMemo(() => {
    const activeIssuedField = ISSUED_FIELD_BY_DAYS[daysFilter] || "issued_30d"
    const activePeriodKey = { "7d": "7D", "30d": "30D", "60d": "60D", "90d": "90D" }[daysFilter] || "30D"

    // Recompute movement levels for the selected period (mirrors backend logic)
    const categoryGroups = new Map()
    for (const item of allItems) {
      const cat = item.category || ""
      if (!categoryGroups.has(cat)) categoryGroups.set(cat, [])
      categoryGroups.get(cat).push(item)
    }

    const dynamicMovementLevel = new Map()
    for (const group of categoryGroups.values()) {
      const moving = group.filter((item) => (item[activeIssuedField] || 0) > 0)
      moving.sort((a, b) => (b[activeIssuedField] || 0) - (a[activeIssuedField] || 0))
      const n = moving.length
      const highCutoff = Math.ceil(n * 0.2)
      const mediumCutoff = Math.ceil(n * 0.5)
      moving.forEach((item, rank) => {
        dynamicMovementLevel.set(item.inventory_id, rank < highCutoff ? "High" : rank < mediumCutoff ? "Medium" : "Low")
      })
      for (const item of group) {
        if (!dynamicMovementLevel.has(item.inventory_id)) {
          dynamicMovementLevel.set(item.inventory_id, "Dead")
        }
      }
    }

    const movementCounts = { High: 0, Medium: 0, Low: 0, Dead: 0 }
    const stockRateCounts = {}
    const categoryTotals = new Map()
    const groupMovement = new Map()

    for (const item of allItems) {
      const level = dynamicMovementLevel.get(item.inventory_id) || "Dead"

      if (movementCounts[level] !== undefined) {
        movementCounts[level] += 1
      }

      const status = item.p0_status || "Unknown"
      stockRateCounts[status] = (stockRateCounts[status] || 0) + 1

      const category = item.category || "Uncategorized"
      if (!categoryTotals.has(category)) {
        categoryTotals.set(category, { category, "7D": 0, "30D": 0, "60D": 0, "90D": 0 })
      }
      const catRow = categoryTotals.get(category)
      catRow["7D"] += item.issued_7d || 0
      catRow["30D"] += item.issued_30d || 0
      catRow["60D"] += item.issued_60d || 0
      catRow["90D"] += item.issued_90d || 0

      const groupKey = isSubCategoryEnabled ? item.sub_category || "Other" : item.category || "Other"
      if (!groupMovement.has(groupKey)) {
        groupMovement.set(groupKey, { name: groupKey, High: 0, Medium: 0, Low: 0, Dead: 0 })
      }
      const groupRow = groupMovement.get(groupKey)
      if (groupRow[level] !== undefined) {
        groupRow[level] += 1
      }
    }

    const movementDistribution = MOVEMENT_LEVELS.map((name) => ({ name, value: movementCounts[name] })).filter(
      (d) => d.value > 0
    )

    const stockRateDistribution = Object.entries(stockRateCounts).map(([name, value]) => ({ name, value }))

    const unitsSoldByCategory = Array.from(categoryTotals.values())
      .sort((a, b) => b[activePeriodKey] - a[activePeriodKey])
      .slice(0, 8)

    const movementByGroup = Array.from(groupMovement.values())
      .sort((a, b) => b.High + b.Medium + b.Low + b.Dead - (a.High + a.Medium + a.Low + a.Dead))
      .slice(0, 8)

    const topItems = allItems
      .filter((i) => (i[activeIssuedField] || 0) > 0)
      .sort((a, b) => (b[activeIssuedField] || 0) - (a[activeIssuedField] || 0))
      .slice(0, 5)

    const velocityTrend = {
      data: ["7D", "30D", "60D", "90D"].map((period) => {
        const row = { period }
        topItems.forEach((item) => {
          row[item.inventory_id] = item[ISSUED_FIELD_BY_DAYS[period.toLowerCase()]] || 0
        })
        return row
      }),
      series: topItems.map((item) => ({ key: item.inventory_id, name: item.item_name })),
    }

    return {
      movementDistribution,
      stockRateDistribution,
      unitsSoldByCategory,
      movementByGroup,
      velocityTrend,
      activePeriodKey,
    }
  }, [allItems, isSubCategoryEnabled, daysFilter])

  const unitsSold = useMemo(() => {
    const field = ISSUED_FIELD_BY_DAYS[daysFilter] || "issued_30d"
    return allItems.reduce((sum, item) => sum + (item[field] || 0), 0)
  }, [allItems, daysFilter])

  return {
    // master data
    categories,
    subCategories,
    subCategoryOptions,
    brandOptions,
    sizes,
    colors,
    isColorEnabled,
    isSubCategoryEnabled,

    // filters
    searchTerm,
    setSearchTerm,
    filterOptions,
    setFilterOptions,
    daysFilter,
    setDaysFilter,

    // tabs
    activeTab,
    setActiveTab,

    // dashboard data
    items,
    summary,
    loading,
    error,

    // pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalCount,

    // analysis data
    analysis,
    analysisLoading,
    analysisError,

    // charts & derived overview data
    charts,
    allItemsLoading,
    unitsSold,
  }
}
